import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Mic, Square, Upload } from "lucide-react";
import useMicRecorder from "@/components/sunovoice/useMicRecorder";
import { MAX_AUDIO_BYTES, clock } from "@/lib/sunoVoicesApi";

const tabCls = (on) => `inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[12px] font-medium transition-colors duration-200 ${on ? "bg-white text-black" : "text-white/60 hover:bg-white/[0.08] hover:text-white"}`;

function RecordPane({ maxSec, onFile, testId, hint }) {
  const rec = useMicRecorder({ maxSec, onRecorded: onFile });
  const live = rec.state === "recording";
  const busy = rec.state === "starting" || rec.state === "saving";
  return (
    <div className="flex flex-col items-center gap-3 py-4 text-center">
      <button type="button" data-testid={`${testId}-record`} data-state={rec.state} onClick={live ? rec.stop : rec.start} disabled={busy}
        className={`grid h-16 w-16 place-items-center rounded-full transition-transform duration-200 hover:scale-105 active:scale-95 disabled:opacity-50 ${live ? "bg-rose-500 shadow-[0_0_0_8px_rgba(244,63,94,0.18)]" : "bg-gradient-to-br from-rose-400 to-red-500"}`}>
        {busy ? <Loader2 className="h-6 w-6 animate-spin" /> : live ? <Square className="h-5 w-5 fill-white" /> : <Mic className="h-6 w-6" />}
      </button>
      <p data-testid={`${testId}-timer`} className="font-mono text-[12px] uppercase tracking-[0.16em] text-white/70">
        {live ? <><span className="mr-2 inline-block h-2 w-2 animate-pulse rounded-full bg-rose-400" />Recording {clock(rec.elapsed)} · press to stop</>
          : rec.state === "starting" ? "Opening your microphone…" : rec.state === "saving" ? "Saving…" : "Press to record"}
      </p>
      <p className="max-w-sm text-[12px] leading-relaxed text-white/45">{hint} Stops on its own at {clock(maxSec)}.</p>
    </div>
  );
}

function UploadPane({ onFile, testId }) {
  const pick = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > MAX_AUDIO_BYTES) { toast.error("That file is over 30 MB", { description: "Use an MP3, or trim the WAV first." }); return; }
    onFile(f);
  };
  return (
    <label className="flex cursor-pointer flex-col items-center gap-2 rounded-2xl border-2 border-dashed border-white/12 py-7 text-center transition-colors hover:border-cyan-300/40">
      <Upload className="h-6 w-6 text-white/60" />
      <span className="text-[13px] font-semibold">Choose an audio file</span>
      <span className="text-[11px] text-white/45">MP3, WAV, M4A, FLAC or OGG · up to 30 MB</span>
      <input type="file" data-testid={`${testId}-file`} accept="audio/*,.mp3,.wav,.m4a,.aac,.flac,.ogg" onChange={pick} className="sr-only" />
    </label>
  );
}

/** Record in the browser or upload a file → `onFile(File)`. */
export default function AudioSourcePicker({ onFile, maxSec = 180, testId, hint }) {
  const [tab, setTab] = useState("record");
  return (
    <div data-testid={testId} className="rounded-2xl border border-white/[0.08] bg-black/30 p-3">
      <div className="flex gap-1">
        <button type="button" data-testid={`${testId}-tab-record`} className={tabCls(tab === "record")} onClick={() => setTab("record")}><Mic className="h-3.5 w-3.5" /> Record now</button>
        <button type="button" data-testid={`${testId}-tab-upload`} className={tabCls(tab === "upload")} onClick={() => setTab("upload")}><Upload className="h-3.5 w-3.5" /> Upload a file</button>
      </div>
      <div className="mt-2">
        {tab === "record" ? <RecordPane maxSec={maxSec} onFile={onFile} testId={testId} hint={hint} /> : <UploadPane onFile={onFile} testId={testId} />}
      </div>
    </div>
  );
}
